import { dateService, FORMATS } from '@/global/services/date'
import { isObject } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import documentsHistoryHelper from '../documents-history-helper'
import tabsHelper from '../tabs-helper'
import switchField from '@/global/components/fields/SwitchField.vue'

export default {
  name: 'basicInfoConfig',

  mixins: [
    documentsHistoryHelper,
    tabsHelper
  ],

  data () {
    return {
      internalDataVisible: false,
      subjectSummaryVisible: false,
      historyVisible: false,
      basicDocumentHistoryLoaded: false,
      basicInfoRefreshShowLoader: false,
      basicInfoRefreshDisabled: false,
      datesSwitchValue: false,
      requestSwitchValue: false,
      showEregulationSignOutButton: false,
      showMarkReadyForInvoicingButton: false,
      basicInfoInternalData: [],
      basicInfoInternalDataDocClassBlock: {
        key: 'doc_class',
        title: this.$t('road-maintenance/aggregator.class') + ':'
      },
      basicInfoInternalDataRegistryNumberBlock: {
        key: 'registry_number',
        title: this.$t('road-maintenance/aggregator.registration_number') + ':'
      }
    }
  },

  computed: {
    originalBasicInfoInternalData () {
      return [
        {
          key: 'document_code',
          title: this.$t('road-maintenance/aggregator.request_id') + ':'
        },
        {
          key: 'source',
          title: this.$t('road-maintenance/aggregator.source') + ':',
          customValue: (data) => {
            return data?.source ? this.$t(data.source) : '-'
          }
        },
        {
          key: 'reference_code',
          title: this.$t('road-maintenance/aggregator.unique_identifier') + ':'
        }
      ]
    },

    basicInfo () {
      return {
        title: this.$t('fleet/vehicle.basic_info'),
        key: 'basic-info',
        visible: this.$route.query.category !== 'request_new',
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/basic-info`,
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/basic-info`,
            method: 'get'
          }
        },
        editUpdate: (event) => this.basicInfoEditUpdated(event),
        dataFetched: (event) => this.onBasicInfoDataFetched(event, false, true),
        afterCancel: (event) => this.onBasicInfoDataFetched(event),
        afterSave: (event) => this.onBasicInfoDataFetched(event, true),
        modifyDataBeforeSend: (event) => this.basicInfoModifyDataBeforeSend(event),
        tabContent: [
          {
            component: this.tabActionsComponent,
            title: this.$t('fleet/vehicle.basic_info'),
            showEditButtons: !!this.permissions?.canUpdate,
            showBackButton: {
              onGoBack: () => this.goBack()
            },
            additional_buttons: [
              {
                component: switchField,
                useBaseLabel: true,
                label: this.$t('road-maintenance/aggregator.allow_request_edit'),
                setValue: this.requestSwitchValue,
                onChange: (event) => this.onEditingSwitchesChange(event, 'enable_request_edit'),
                visible: !!this.permissions?.canUpdate
              },
              {
                component: switchField,
                useBaseLabel: true,
                label: this.$t('road-maintenance/aggregator.allow_dates_edit'),
                setValue: this.datesSwitchValue,
                onChange: (event) => this.onEditingSwitchesChange(event, 'enable_date_edit'),
                visible: !!this.permissions?.canUpdate
              },
              {
                type: 'base',
                prependIcon: 'mdi-logout-variant',
                prependIconStyle: {
                  fontSize: '18px !important'
                },
                visible: this.showEregulationSignOutButton,
                label: this.$t('road-maintenance/aggregator.eregulation_sign_out'),
                click: (event) => this.onERegulationSignOut(event)
              },
              {
                type: 'base',
                prependIcon: 'mdi-invoice-check-outline',
                prependIconStyle: {
                  fontSize: '18px !important'
                },
                visible: this.showMarkReadyForInvoicingButton,
                label: this.$t('road-maintenance/aggregator.mark_ready_for_invoicing'),
                click: (event) => this.onMarkReadyForInvoicing(event)
              },
              {
                type: 'base',
                appendIcon: 'mdi-refresh',
                disabled: this.basicInfoRefreshDisabled,
                click: async (event) => {
                  const { fetchTabData } = event || {}
                  this.basicInfoRefreshDisabled = true

                  if (typeof fetchTabData === 'function') {
                    fetchTabData()
                  }

                  await this.fetchDocumentsHistory()
                  setTimeout(() => {
                    this.basicInfoRefreshDisabled = false
                  }, this.refreshTabBtnTimeout)
                }
              }
            ]
          },
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-internal-blocks-main',
            titleClasses: 'aggregator-requests-internal-title',
            title: this.$t('road-maintenance/aggregator.internal_data'),
            visible: this.internalDataVisible,
            columnsConfig: this.basicInfoInternalData,
            dataKey: 'internal_data'
          },
          {
            component: this.columnsContentComponent,
            key: 'basic-data',
            type: 'columns',
            rows: [
              {
                type: 'fields',
                disabled: !this.permissions?.canUpdate,
                columnsCount: 3,
                fields: [
                  {
                    key: 'request_type_id',
                    label: this.$t('road-maintenance/aggregator.type_of_request') + '*',
                    type: 'autocomplete',
                    items: this.dropdownValues?.request_type ?? [],
                    autocomplete_options: {
                      allowSearch: false,
                      custom_display_value: (item) => {
                        return item && item.description ? this.$t(item.description) : null
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'processing_entity_id',
                    label: this.$t('road-maintenance/aggregator.responsible_for_processing') + '*',
                    type: 'autocomplete',
                    items: this.dropdownValues?.processing_entity ?? [],
                    autocomplete_options: {
                      allowSearch: false,
                      custom_display_value: (item) => {
                        return item && item.description ? this.$t(item.description) : null
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'employee_id',
                    label: this.$t('road-maintenance/aggregator.delegated') + '*',
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'road-maintenance',
                      route: 'traffic-regulation/employees',
                      custom_display_value: (item) => {
                        return item?.full_name ? item.full_name : null
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'status_id',
                    label: this.$t('road-maintenance/excavation-service.status') + '*',
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'road-maintenance',
                      route: 'traffic-regulation/statuses',
                      allowSearch: false,
                      fetchInitial: false,
                      // Make search when a current status value is set the first time (without this callback, in queryParams callback we don't have a current status set yet), because we need to filter statuses by current status
                      onDataLoad: async (event) => {
                        const { makeSearch, items } = event || {}

                        if (typeof makeSearch === 'function' && (!items || !items.length)) {
                          await makeSearch()
                        }
                      },
                      // Set the current status id to the query params
                      queryParams: (event) => {
                        const { id } = event?.selectedValue || {}

                        return {
                          status_id: id || null
                        }
                      },
                      custom_display_value: (value) => {
                        const { description } = value || {}

                        return description ? this.$t(description) : null
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'date_time',
                    type: 'datetime-picker',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.datetimeShort,
                    placeholder: FORMATS.datetimeShort,
                    label: this.$t('road-maintenance/aggregator.request_created_date') + '*',
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.datetimeShort, true)) {
                        updateValidationErrors({
                          date_time: this.$t('base.invalid_date').replace(':format', FORMATS.datetimeShort)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'parent_id',
                    label: this.$t('road-maintenance/aggregator.related_subject') + '*',
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'road-maintenance',
                      route: 'traffic-regulation/autocomplete'
                    },
                    creatable: true,
                    editable: true,
                    visible: true
                  }
                ]
              }
            ]
          },
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.subject_summary'),
            visible: this.subjectSummaryVisible,
            externalData: this.logsData,
            dataKey: 'summary',
            columnsConfig: [
              {
                key: 'created_at',
                title: this.$t('road-maintenance/aggregator.request_created_date') + ':',
                customValue: (data) => {
                  return data?.created_at ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              {
                key: 'type',
                title: this.$t('road-maintenance/aggregator.request_type') + ':',
                customValue: (data) => {
                  return data?.type ? this.$t(data.type) : '-'
                }
              },
              {
                key: 'user',
                title: this.$t('road-maintenance/aggregator.request_by') + ':'
              }
            ]
          },
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.history'),
            visible: this.historyVisible,
            externalData: this.logsData,
            dataKey: 'history',
            columnsConfig: [
              {
                key: 'created_at',
                title: this.$t('road-maintenance/aggregator.request_created_date') + ':',
                customValue: (data) => {
                  return data?.created_at ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              {
                key: 'type',
                title: this.$t('road-maintenance/aggregator.request_type') + ':',
                customValue: (data) => {
                  return data?.type ? this.$t(data.type) : '-'
                }
              },
              {
                key: 'user',
                title: this.$t('road-maintenance/aggregator.request_by') + ':'
              },
              {
                key: 'edited_fields',
                visible: (event) => {
                  const { blockValue } = event || {}
                  return blockValue !== '-'
                },
                style: {
                  overflow: 'hidden',
                  height: 'auto',
                  flexGrow: 1,
                  minWidth: 'unset'
                },
                title: this.$t('road-maintenance/aggregator.edited_fields') + ':'
              }
            ]
          }
        ]
      }
    }
  },

  created () {
    this.basicInfoInternalData = [...this.originalBasicInfoInternalData]
  },

  methods: {
    onMarkReadyForInvoicing (event) {
      const { fetchTabData } = event || {}
      this.$set(this.informationDialogConfig, 'title', this.$t('road-maintenance/aggregator.mark_ready_for_invoicing_title'))
      this.$set(this.informationDialogConfig, 'message', this.$t('road-maintenance/aggregator.mark_ready_for_invoicing_message'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.yes'))
      this.$set(this.informationDialogConfig, 'negativeButtonText', this.$t('base.no'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', async () => {
        try {
          this.$set(this.informationDialogConfig, 'showLoader', true)
          await api('road-maintenance').post(`traffic-regulation/${this.$route.params.id}/mark-ready-for-invoicing`, {})
          this.$set(this.informationDialogConfig, 'showDialog', false)
          if (typeof fetchTabData === 'function') {
            fetchTabData()
          }
        }
        catch (exception) {
          console.error(exception)
        }
        finally {
          this.$set(this.informationDialogConfig, 'showLoader', false)
        }
      })
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    async onERegulationSignOut (event) {
      const { fetchTabData, formData } = event || {}
      const { reference_code: referenceCode } = formData?.internal_data?.[0] || {}
      const { completion_date: completionDate } = formData || {}

      this.$set(this.eRegulationSignOutDialogConfig, 'config', {
        fetchTabData,
        referenceCode,
        completionDate
      })
      this.$set(this.eRegulationSignOutDialogConfig, 'show', true)
    },

    basicInfoEditUpdated (event) {
      const { edit } = event || {}
      this.edit = !!edit

      if (this.edit) {
        this.internalDataVisible = false
        this.subjectSummaryVisible = false
        this.historyVisible = false
      }
      else {
        this.internalDataVisible = true
        this.subjectSummaryVisible = true
        this.historyVisible = true
      }
    },

    async onBasicInfoDataFetched (event, afterSave = false, onFetched = false) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}

      const data = apiData?.data || formDataForSend || {}

      // map form data on data fetch
      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          // Modify a date_time date to be in format: FORMATS.datetimeShort
          date_time: data?.date_time ? dateService.formatCustom(data.date_time, FORMATS.isoDatetime, FORMATS.datetimeShort) : dateService.now(FORMATS.datetimeShort),
          request_type_id: data?.request_type?.id || data?.request_type_id || null,
          road_type_id: data?.road_type?.id || data?.road_type_id || null,
          status_id: data?.status?.id || data?.status_id || null,
          employee_id: data?.employee?.id || data?.employee_id || null
        })
      }

      // append document classes and registry numbers blocks dynamically
      if (onFetched && data?.internal_data?.[0]?.vendor_details?.length) {
        // reset to original
        this.basicInfoInternalData = [...this.originalBasicInfoInternalData]
        // add dynamic internal data (vendor details)
        data.internal_data[0].vendor_details.forEach(vendor => {
          const { doc_class: docClass, registry_number: registryNumber } = vendor || {}
          const docClassBlock = {
            ...this.basicInfoInternalDataDocClassBlock,
            customValue: () => docClass || '-'
          }

          const registryNumberBlock = {
            ...this.basicInfoInternalDataRegistryNumberBlock,
            customValue: () => registryNumber || '-'
          }

          this.basicInfoInternalData.push(docClassBlock, registryNumberBlock)
        })
      }

      // set date and request switches values
      this.requestSwitchValue = !!data?.enable_request_edit
      this.datesSwitchValue = !!data?.enable_date_edit
      // set e-regulation sign out button visibility
      this.showEregulationSignOutButton = !!data?.show_regulation_cancellation_invitation_create_btn
      // set mark ready for invoicing button visibility
      this.showMarkReadyForInvoicingButton = !!data?.show_mark_ready_for_invoicing_btn
      // set current e-regulation status id to global variable (used in other tabs)
      this.globalSelectedStatusId = data?.status?.id || data?.status_id || null

      // fetch documents history initially or after document save
      if (!this.basicDocumentHistoryLoaded || afterSave) {
        await this.fetchDocumentsHistory()
        this.basicDocumentHistoryLoaded = true
      }
    },

    async fetchDocumentsHistory () {
      try {
        const { id } = this.$route?.params || {}

        if (id) {
          const { data } = await api('road-maintenance').get(`traffic-regulation/${id}/logs`)
          this.logsData = {
            ...this.logsData,
            ...data
          }
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    basicInfoModifyDataBeforeSend (event) {
      const { originalData } = event || {}

      if (originalData) {
        originalData.date_time = originalData.date_time ? dateService.formatCustom(originalData.date_time, FORMATS.datetimeShort, FORMATS.isoDatetime) : dateService.now(FORMATS.isoDatetime)
      }

      return originalData
    },

    async onEditingSwitchesChange (event, queryParamKey = '') {
      try {
        const { value } = event || {}

        if (!queryParamKey) {
          return
        }

        const { data } = await api('road-maintenance').patch(`traffic-regulation/${this.$route.params.id}/change-editing-status`, {
          [queryParamKey]: !!value
        })
        this.requestSwitchValue = !!data?.enable_request_edit
        this.datesSwitchValue = !!data?.enable_date_edit
      }
      catch (exception) {
        console.error(exception)
      }
    }
  }
}
